import React, { Fragment } from 'react'
import MomentLocaleUtils, { formatDate, parseDate } from 'react-day-picker/moment'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import styles from './filters.scss'
import { withMetricUnits } from 'common/units/MetricUnitsContext'
import { RangeInput } from 'common/styleguide/RangeInput'
import { YearMonthForm } from 'common/styleguide/DatePickerInput'
import { CheckboxGroup } from 'common/styleguide/CheckboxGroup'
import { readDateFormatFromPreferences } from 'lib/dateUtils'
import { j } from 'lib/textUtils'
import { Msg, fm, commonMetrics } from 'lib/localization'
import { unitMessages } from 'lib/units/unitTypes'
import smartDate from '@qrohlf/smart-date'
import { Set } from 'immutable'
import PropTypes from 'prop-types'
import moment from 'moment'
import 'moment/locale/es'
import { defineMessages, injectIntl } from 'react-intl'

const Value = ({ children }) => (
  <div className={styles.value}>
    {children}
  </div>
)

const filterMessages = defineMessages({
  after: {
    id: 'filterMessages.after',
    defaultMessage: 'after {date}'
  },
  any: {
    id: 'filterMessages.any',
    defaultMessage: 'any {metricType}'
  },
  before: {
    id: 'filterMessages.before',
    defaultMessage: 'before {date}'
  },
  clear: {
    id: 'filterMessages.clear',
    defaultMessage: 'Clear'
  },
  dates: {
    id: 'filterMessages.dates',
    defaultMessage: 'dates'
  },
  lessThan: {
    id: 'filterMessages.lessThan',
    defaultMessage: 'less than {max} {unitLabel}'
  },
  moreThan: {
    id: 'filterMessages.moreThan',
    defaultMessage: 'more than {min} {unitLabel}'
  },
  from: {
    id: 'filterMessages.from',
    defaultMessage: 'from'
  },
  to: {
    id: 'filterMessages.to',
    defaultMessage: 'to'
  }
})

// I'd like to put this in a more general spot, but the logical place
// (search/utils) has a conflicting implementation - it treats the range
// {min: minValue, max: maxValue} as representing an unbounded search, rather
// than {min: undefined, max: undefined}.
//
// The TODO here is to refactor the LoadExisting filter implementation
// to:
// - use APIv3.search
// - keep a list of filters[] and derive the query like we do in Rides
//   rather than mutating the query and kicking off searches in each
//   mutating component
// - use {min: undefined, max: 1234} semantics for an open-ended query
//
// and then we can use the same formatRangeLabel function everywhere.
export const formatRangeLabel = ({ min, max, unitLabel, metricType }) => {
  if (min === undefined && max === undefined) {
    return <Msg m={filterMessages.any} values={{ metricType }} />
  } else if (min === undefined) {
    return <Msg m={filterMessages.lessThan} values={{ max, unitLabel }} />
  } else if (max === undefined) {
    return <Msg m={filterMessages.moreThan} values={{ min, unitLabel }} />
  } else {
    return `${min}-${max} ${unitLabel}`
  }
}

export const formatDateRangeLabel = (min, max) => {
  const formatMin = moment(min)
  const formatMax = moment(max)
  if (min === undefined && max === undefined) {
    return <Msg m={filterMessages.any} values={{ metricType: <Msg m={filterMessages.dates} /> }} />
  } else if (min === undefined) {
    return <Msg m={filterMessages.before} values={{ date: smartDate(formatMax, { includeYear: true, includeTime: false }) }} />
  } else if (max === undefined) {
    return <Msg m={filterMessages.after} values={{ date: smartDate(formatMin, { includeYear: true, includeTime: false }) }} />
  } else {
    return smartDate([formatMin, formatMax], { includeYear: true, includeTime: false })
  }
}

class QuadraticRangeFilter extends React.PureComponent {
  constructor (props) {
    super(props)
    const { defaultValue, minValue, maxValue } = props
    const min = (!defaultValue || defaultValue.min === undefined) ? minValue : defaultValue.min
    const max = (!defaultValue || defaultValue.max === undefined) ? maxValue : defaultValue.max

    this.state = {
      // this is the linear value that controls the slider. we have fixed step value to 1, so we round here.
      // undefined is not acceptable, so we represent the ends of the slider by using the min and max values of the range
      inputValue: {
        min: Math.round(this.unscale(min)),
        max: Math.round(this.unscale(max))
      },
      // this is the value that we display to the user that is quadratically scaled
      // undefined represents our unbounded ends, which is why we have to juggle undefined values in a few places in this component
      scaledValue: {
        min: min === minValue ? undefined : min,
        max: max === maxValue ? undefined : max
      }
    }
  }

  unscale (x) {
    const { minValue, maxValue } = this.props

    const diff = maxValue - minValue
    return Math.sqrt((x - minValue) / diff) * diff + minValue
  }

  scale (x) {
    const { minValue, maxValue } = this.props

    const diff = maxValue - minValue
    return Math.pow((x - minValue) / diff, 2) * diff + minValue
  }

  onChange = ({ min, max }) => {
    const { minValue, maxValue } = this.props

    let scaledMin = Math.round(this.scale(min))
    let scaledMax = Math.round(this.scale(max))
    if (scaledMin === minValue) scaledMin = undefined
    if (scaledMax === maxValue) scaledMax = undefined

    this.setState({
      inputValue: { min, max },
      scaledValue: {
        min: scaledMin,
        max: scaledMax
      }
    })
  }

  onApply = () => {
    const { scaledValue } = this.state
    if (scaledValue.min === undefined && scaledValue.max === undefined) {
      this.props.onClear()
    } else {
      this.props.onApply(scaledValue)
    }
  }

  formatLabel = () => {
    const { unitLabel, metricType } = this.props
    const { min, max } = this.state.scaledValue
    return formatRangeLabel({ min, max, unitLabel, metricType })
  }

  render () {
    const { onClear, minValue, maxValue } = this.props
    const { inputValue } = this.state

    return (
      <Fragment>
        <RangeInput
          minValue={minValue}
          maxValue={maxValue}
          step={1}
          value={inputValue}
          style={{ width: '180px' }}
          onChange={this.onChange}
          onChangeComplete={this.onApply} />
        <Value>
          {this.formatLabel()}
        </Value>
        <a className={styles.apply} onClick={onClear}><Msg m={filterMessages.clear} /></a>
      </Fragment>
    )
  }
}

class RangeFilter extends React.PureComponent {
  static defaultProps = {
    step: 1
  }

  constructor (props) {
    super(props)

    this.state = {
      value: this.transformInitialDefault()
    }
  }

  transformInitialDefault = () => {
    const { defaultValue } = this.props
    return {
      min: (!defaultValue || defaultValue.min === undefined) ? this.minValue() : defaultValue.min,
      max: (!defaultValue || defaultValue.max === undefined) ? this.maxValue() : defaultValue.max
    }
  }

  onApply = () => {
    const value = {
      min: this.computedMin(),
      max: this.computedMax()
    }
    if (value.min === undefined && value.max === undefined) {
      this.props.onClear()
    } else {
      this.props.onApply(value)
    }
  }

  computedMin = () => {
    const { value } = this.state

    return value.min < this.props.minValue
      ? undefined
      : value.min
  }

  // map maxValue to undefined if allowUnset is true
  computedMax = () => {
    const { value } = this.state

    return value.max > this.props.maxValue
      ? undefined
      : value.max
  }

  minValue = () => {
    const { allowUnset, minValue, step } = this.props
    return allowUnset
      ? minValue - step
      : minValue
  }

  maxValue = () => {
    const { allowUnset, maxValue, step } = this.props
    return allowUnset
      ? maxValue + step
      : maxValue
  }

  formatLabel = () => {
    const { unitLabel, metricType } = this.props
    const min = this.computedMin()
    const max = this.computedMax()
    return formatRangeLabel({ min, max, unitLabel, metricType })
  }

  render () {
    const { step, onClear } = this.props
    const { value } = this.state

    return (
      <Fragment>
        <RangeInput
          minValue={this.minValue()}
          maxValue={this.maxValue()}
          step={step}
          value={value}
          style={{ width: '180px' }}
          onChange={value => this.setState({ value })}
          onChangeComplete={this.onApply} />
        <Value>
          {this.formatLabel()}
        </Value>
        <a className={styles.apply} onClick={onClear}><Msg m={filterMessages.clear} /></a>
      </Fragment>
    )
  }
}

class _DateRangeFilter extends React.PureComponent {
  constructor (props) {
    super(props)

    const { value } = props
    this.state = {
      monthMin: value && value.min ? new Date(value.min) : new Date(),
      monthMax: value && value.max ? new Date(value.max) : new Date(),
      value: {
        min: value && value.min ? new Date(value.min) : undefined,
        max: value && value.max ? new Date(value.max) : undefined
      }
    }
  }

  // keep dates around in ISO, not whatever format the user happens to be into
  onApply = () => {
    const [min, max] = [this.computedMin(), this.computedMax()]
    const value = {
      min: min ? moment(min).toISOString() : undefined,
      max: max ? moment(max).toISOString() : undefined
    }
    if (value.min === undefined && value.max === undefined) {
      this.props.onClear()
    } else {
      this.props.onApply(value)
    }
  }

  computedMin = () => {
    const { value } = this.state

    return value.min <= this.props.minValue
      ? undefined
      : value.min
  }

  computedMax = () => {
    const { value } = this.state

    return value.max >= this.props.maxValue
      ? undefined
      : value.max
  }

  formatLabel = () => {
    const min = this.computedMin()
    const max = this.computedMax()
    return formatDateRangeLabel(min, max)
  }

  showFromMonth = () => {
    const { value: { min, max } } = this.state
    if (!min) {
      return
    }
    if (moment(max).diff(moment(min), 'months') < 1) {
      this.max.getDayPicker().showMonth(min)
    }
  }

  handleMinChange = (min) => {
    const max = this.state.value.max
    if (max && min > max) {
      this.setState({ value: { min } }, () => this.onApply())
    } else {
      this.setState({ value: { min, max } }, () => this.onApply())
    }
  }

  handleMaxChange = (max) => {
    const min = this.state.value.min
    if (min && min > max) {
      this.setState({ value: { min: max, max: min } }, () => this.onApply())
    } else {
      this.setState({ value: { min, max } }, () => this.onApply())
    }
  }

  render () {
    const { value: { min, max }, monthMin, monthMax } = this.state
    const { formatStr, intl } = this.props
    const highlighted = { after: min, before: max }
    // These *should* be styleguide/DatePickerInput components, however
    // there are major issues that arise from nesting a react-overlays <Overlay/>
    // component inside of another react-overlays <Overlay/> component.
    //
    // using DayPickerInput causes the date picker to use its own overlay
    // implementation that doesn't conflict with the react-overlays system.
    return (
      <Fragment>
        <DayPickerInput
          value={min}
          placeholder={fm(intl, filterMessages.from)}
          format={formatStr}
          formatDate={formatDate}
          parseDate={parseDate}
          dayPickerProps={{
            locale: intl.locale,
            localeUtils: MomentLocaleUtils,
            month: monthMin,
            selectedDays: [min, max],
            toMonth: max,
            modifiers: highlighted,
            numberOfMonths: 1,
            onDayClick: () => this.max.getInput().focus(),
            captionElement: ({ date }) => (
              <YearMonthForm
                date={date}
                onChange={(monthMin) => this.setState({ monthMin })}
              />
            )
          }}
          onDayChange={this.handleMinChange} />
        {' - '}
        <span><DayPickerInput
          ref={el => (this.max = el)}
          value={max}
          placeholder={fm(intl, filterMessages.to)}
          format={formatStr}
          formatDate={formatDate}
          parseDate={parseDate}
          dayPickerProps={{
            locale: intl.locale,
            localeUtils: MomentLocaleUtils,
            month: monthMax,
            selectedDays: [min, max],
            modifiers: highlighted,
            fromMonth: min,
            numberOfMonths: 1,
            captionElement: ({ date }) => (
              <YearMonthForm
                date={date}
                onChange={(monthMax) => this.setState({ monthMax })}
              />
            )
          }}
          onDayChange={this.handleMaxChange} />
        </span>
        <Value>
          {this.formatLabel()}
        </Value>
        <a className={styles.apply} onClick={() => this.props.onClear()}><Msg m={filterMessages.clear} /></a>
      </Fragment>
    )
  }
}

const DateRangeFilter = injectIntl(_DateRangeFilter)

export const LengthInput = injectIntl(withMetricUnits((props) => (
  <QuadraticRangeFilter
    {...props}
    minValue={0}
    maxValue={props.metricUnits ? 1000 : 500}
    unitLabel={fm(props.intl, props.metricUnits ? unitMessages.kmShort : unitMessages.miLong)}
    metricType={<Msg lowerCase m={commonMetrics.distance} />}
  />
)))

export const ElevationInput = injectIntl(withMetricUnits((props) => (
  <RangeFilter
    {...props}
    allowUnset
    minValue={0}
    step={100}
    maxValue={props.metricUnits ? 5000 : 10000}
    unitLabel={fm(props.intl, props.metricUnits ? unitMessages.mShort : unitMessages.ftShort)}
    metricType={<Msg lowerCase m={commonMetrics.elevation} />}
  />
)))

export const DurationInput = injectIntl(withMetricUnits((props) => (
  <RangeFilter
    {...props}
    allowUnset
    minValue={0}
    maxValue={24}
    unitLabel={fm(props.intl, unitMessages.hrsShort)}
    metricType={<Msg m={commonMetrics.duration} />}
  />
)))

export const SpeedInput = injectIntl(withMetricUnits((props) => (
  <RangeFilter
    {...props}
    allowUnset
    minValue={0}
    maxValue={100}
    unitLabel={fm(props.intl, props.metricUnits ? unitMessages.kphShort : unitMessages.mphShort)}
    metricType={<Msg lowerCase m={commonMetrics.speed} />}
  />
)))

export const WattsInput = injectIntl(withMetricUnits((props) => (
  <RangeFilter
    {...props}
    allowUnset
    minValue={0}
    maxValue={1000}
    unitLabel={fm(props.intl, commonMetrics.watts)}
    metricType={<Msg lowerCase m={commonMetrics.power} />}
  />
)))

export const DateInput = injectIntl(withMetricUnits((props) => {
  const formatStr = readDateFormatFromPreferences()
  return <DateRangeFilter
    {...props}
    formatStr={formatStr}
    minValue={moment('2009-01-01', formatStr)}
    maxValue={moment(null, formatStr)}
  />
}))

export const formatCheckboxLabel = (values, map) => {
  const mappedValues = values.map(v => map.get(v))
  return j(mappedValues)
}

export class CheckboxInput extends React.PureComponent {
  static propTypes = {
    onApply: PropTypes.func.isRequired,
    onClear: PropTypes.func
  }

  constructor (props) {
    super(props)

    this.state = {
      initalValue: props.defaultValue || Set(),
      internalValue: props.defaultValue || Set()
    }
  }

  onToggleItem = (selected) => {
    this.setState({ internalValue: selected }, () => this.applyOrClear())
  }

  applyOrClear = () => {
    const { onApply, onClear } = this.props
    const { internalValue } = this.state

    if (internalValue.size === 0 && onClear) {
      onClear()
    } else {
      onApply(internalValue)
    }
  }

  render () {
    const { options } = this.props
    const { initalValue, internalValue } = this.state

    return <Fragment>
      <CheckboxGroup
        style={{ marginBottom: '5px' }}
        horizontal
        options={options}
        selected={internalValue}
        onChange={this.onToggleItem} />
      <a className={styles.apply} onClick={() => this.setState({ internalValue: initalValue }, () => this.applyOrClear())}><Msg m={filterMessages.clear} /></a>
    </Fragment>
  }
}
